import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import OrganizationSelect from "./organizationSelect";
import { useAuth } from "../contexts/authContext";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { Locale, UIState } from "../types/ui-types";
import { QueryClient } from "@tanstack/react-query";

const SideNavContainer = styled.div`
  height: 100vh;
  width: var(--sidenav-width);
  background-color: var(--sumi-sidenav-background);
  color: var(--text-color);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 20px;
  overflow: auto;
  gap: 15px;
`;

const NavItem = styled.a<{ $isActive: boolean }>`
  padding: 10px 15px;
  text-decoration: none;
  color: ${({ $isActive }) =>
    $isActive ? "var(--text-color-primary)" : "var(--text-color-primary)"};
  width: 100%;
  text-align: center;
  transition: background-color 0.3s;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 10px;
  align-items: center;
  height: 36px;
  white-space: nowrap;
`;

const Logo = styled.h1`
  color: var(--sumi-blue);
  font-size: 24px;
  margin-bottom: 0;
  font-weight: 700;
  padding: 0 1.5rem;
  text-align: left;
  width: 100%;
  letter-spacing: -0.5px;
`;

const LinkContainer = styled.div<{
  $isActive: boolean;
  $darkTheme: boolean;
  $smallerText?: boolean;
}>`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  background-color: ${({ $isActive }) =>
    $isActive ? "var(--sumi-blue)" : "transparent"};
  color: ${({ $isActive, $darkTheme }) =>
    $isActive
      ? "#ffffff"
      : $darkTheme
        ? "#ffffff"
        : "var(--text-color-primary)"};
  padding: 8px 13px;
  border-radius: 5px;
  width: 100%;
  line-height: 2rem;
  height: 36px;
  font-size: ${({ $smallerText }) => ($smallerText ? "1rem" : "1rem")};

  &:hover {
    background-color: var(--sumi-blue);
    color: #ffffff;
    transition:
      background-color 0.4s,
      color 0.4s;
    cursor: pointer;
  }
`;

const Logout = styled.div`
  margin-top: auto;
  margin-bottom: 20px;
`;

const SideNav: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    logout,
    isAdmin,
    isAdminForSelectedCompanyOrOrganization,
    canReadReportsForSelectedCompanyOrOrganization,
    companyRightsHasLoaded,
  } = useAuth();
  const queryClient = new QueryClient();

  // Check if we're on the report edit route
  const isReportEditRoute = useMemo(() => {
    const reportRoutePattern =
      /^\/(report|detailed-report|light-report|new-report|new-basic-report)(\/\d+)?$/;
    return reportRoutePattern.test(location.pathname);
  }, [location.pathname]);

  const [
    locale,
    organizationId,
    setAzureAdCode,
    setBearerToken,
    setIsLoggedIn,
    setOrganizationId,
    setCompanyId,
    setCompanyName,
    setOrganizationName,
    theme,
    companyId,
  ] = useUIStore(
    useShallow((state: UIState) => [
      state.locale,
      state.organizationId,
      state.setAzureAdCode,
      state.setBearerToken,
      state.setIsLoggedIn,
      state.setOrganizationId,
      state.setCompanyId,
      state.setCompanyName,
      state.setOrganizationName,
      state.theme,
      state.companyId,
    ]),
  );

  const isActive = (path: string) => {
    if (path === "/" && window.location.pathname !== "/") {
      return false;
    }

    const splitPathname = window.location.pathname.split("/");

    return splitPathname.includes(path.replace("/", ""));
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setBearerToken(undefined);
    setAzureAdCode(undefined);
    setOrganizationId(null);
    setCompanyId(null);
    setCompanyName(null);
    setOrganizationName(null);
    queryClient.clear();
    queryClient.invalidateQueries({
      queryKey: ["companies"],
    });
    logout();
  };

  // Check permissions when company or organization changes
  React.useEffect(() => {
    if (
      companyRightsHasLoaded &&
      !(canReadReportsForSelectedCompanyOrOrganization() || isAdmin)
    ) {
      // If user cannot read reports and is on a reports page, redirect to home
      if (location.pathname.includes("/reporting")) {
        console.log("Redirecting to home due to insufficient permissions");
        navigate("/");
      }
    }
  }, [
    companyId,
    organizationId,
    canReadReportsForSelectedCompanyOrOrganization,
    location.pathname,
    navigate,
    companyRightsHasLoaded,
  ]);

  const showReporting = canReadReportsForSelectedCompanyOrOrganization();

  return (
    <SideNavContainer>
      <Logo>
        <img
          src={theme === "dark" ? "/sumion-white.svg" : "/sumion-blue.svg"}
          width={150}
        />
      </Logo>
      {!isAdmin ? <OrganizationSelect disabled={isReportEditRoute} /> : <br />}
      <NavItem
        id="nav-link-summary"
        href="/"
        onClick={(e) => {
          e.preventDefault();
          navigate("/");
        }}
        $isActive={isActive("/")}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            navigate("/");
          }
        }}
      >
        <LinkContainer
          $isActive={isActive("/")}
          $darkTheme={theme === "dark"}
          $smallerText={locale === Locale.SWE}
        >
          <i className="pi pi-home" />
          {t("label-nav-summary")}
        </LinkContainer>
      </NavItem>

      {/* <NavItem
        id="nav-new-report"
        onClick={(e) => {
          e.preventDefault();
          navigate("/reporting-choice");
        }}
        $isActive={isActive("/reporting-choice") || isActive("/report")}
      >
        <LinkContainer
          $isActive={isActive("/reporting-choice") || isActive("/report")}
        >
          <i className="pi pi-file-plus" />
          {t("label-nav-new-report")}
        </LinkContainer>
      </NavItem> */}

      {(showReporting || isAdmin) && (
        <NavItem
          id="nav-link-reporting"
          href="/reporting"
          onClick={(e) => {
            e.preventDefault();
            navigate("/reporting");
          }}
          $isActive={isActive("/reporting")}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              navigate("/reporting");
            }
          }}
        >
          <LinkContainer
            $isActive={isActive("/reporting")}
            $darkTheme={theme === "dark"}
            $smallerText={locale === Locale.SWE}
          >
            <i className="pi pi-chart-bar" />
            {t("label-nav-reporting")}
          </LinkContainer>
        </NavItem>
      )}

      {/* Models and products only displayed for customers */}
      {!isAdmin && (
        <NavItem
          id="nav-link-models"
          href="/models"
          onClick={(e) => {
            e.preventDefault();
            navigate("/models");
          }}
          $isActive={isActive("/models")}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              navigate("/models");
            }
          }}
        >
          <LinkContainer
            $isActive={isActive("/models")}
            $darkTheme={theme === "dark"}
            $smallerText={locale === Locale.SWE}
          >
            <i className="pi pi-sitemap" />
            {t("label-nav-models")}
          </LinkContainer>
        </NavItem>
      )}

      <NavItem
        id="nav-link-organizations"
        href="/organizations"
        onClick={(e) => {
          e.preventDefault();
          navigate("/organizations");
        }}
        $isActive={isActive("/organizations")}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            navigate("/organizations");
          }
        }}
      >
        <LinkContainer
          $isActive={isActive("/organizations")}
          $darkTheme={theme === "dark"}
          $smallerText={locale === Locale.SWE}
        >
          <i className="pi pi-building" />
          {isAdmin
            ? t("label-nav-organizations-admin")
            : t("label-nav-organizations")}
        </LinkContainer>
      </NavItem>
      {(isAdmin || isAdminForSelectedCompanyOrOrganization()) && (
        <NavItem
          id="nav-link-users"
          href="/users"
          onClick={(e) => {
            e.preventDefault();
            navigate("/users");
          }}
          $isActive={isActive("/users")}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              navigate("/users");
            }
          }}
        >
          <LinkContainer
            $isActive={isActive("/users")}
            $darkTheme={theme === "dark"}
            $smallerText={locale === Locale.SWE}
          >
            <i className="pi pi-users" />
            {t("label-nav-users")}
          </LinkContainer>
        </NavItem>
      )}
      {isAdmin && (
        <NavItem
          id="nav-link-admin-invoicing"
          href="/admin-invoicing"
          onClick={(e) => {
            e.preventDefault();
            navigate("/admin-invoicing");
          }}
          $isActive={isActive("/admin-invoicing")}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              navigate("/admin-invoicing");
            }
          }}
        >
          <LinkContainer
            $isActive={isActive("/admin-invoicing")}
            $darkTheme={theme === "dark"}
            $smallerText={locale === Locale.SWE}
          >
            <i className="pi pi-envelope" />
            {t("header-admin-invoicing")}
          </LinkContainer>
        </NavItem>
      )}

      <Logout>
        <NavItem
          id="nav-logout"
          onClick={(e) => {
            e.preventDefault();
            handleLogout();
          }}
          $isActive={false}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleLogout();
            }
          }}
        >
          <LinkContainer
            $isActive={false}
            $darkTheme={theme === "dark"}
            $smallerText={locale === Locale.SWE}
          >
            <i className="pi pi-sign-out" />
            {t("label-nav-logout")}
          </LinkContainer>
        </NavItem>
      </Logout>
    </SideNavContainer>
  );
};

export default SideNav;
